import type { OfferMatch } from "@/app/nft/sell/use-sell-state"
import { getBaseNftEventContext } from "@/app/nft/utils/get-base-nft-event-context"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import { LoggerMolecule } from "@/instrumentation/logger/logger-molecule"
import { type EventContext, Flow } from "@/instrumentation/logger/types"
import * as amplitude from "@amplitude/analytics-browser"
import type { NftMiniature } from "@anaconda/core/nft/types"
import {
  type AcceptCollectionBidRequest,
  type Blockchain,
  type ItemId,
  type Order,
  acceptCollectionBidFactoryReatom,
} from "@rarible_int/anaconda_sdk/ethereum"
import type { ReatomEvmContainer } from "@rarible_int/anaconda_sdk/ethereum"
import { sonnerToast } from "@rarible_int/anaconda_ui_components"
import { action, atom, withAsync, withCallHook, wrap } from "@reatom/core"
import type { Atom } from "@reatom/core"
import { molecule } from "bunshi"
import { EvmSdkContainerMoleculeReatom } from "../../blockchains/evm-sdk-container-molecule.reatom"
import { AcceptCollectionBidToast } from "../../toasts/accept-collection-bid-toast"
import {
  TransactionEvent,
  getTransactionErrorMessage,
} from "../events/transaction-event"

export interface AcceptCollectionBidActionParams {
  selectedNftsAtom: Atom<NftMiniature[]> | (() => NftMiniature[])
  offerByNftAtom:
    | Atom<Record<ItemId, OfferMatch>>
    | (() => Record<ItemId, OfferMatch>)
  blockchain: Blockchain
  // Captured data at click time (used when dialog closes and atoms are destroyed)
  capturedSelectedNfts?: NftMiniature[]
  capturedOfferByNft?: Record<ItemId, OfferMatch>
}

export interface AcceptCollectionBidActionResult {
  successfulTransactions: Array<{ itemId: ItemId; txId: string }>
  errorTransactions: Array<{ itemId: ItemId; error: Error }>
}

export const AcceptCollectionBidTransactionMoleculeReatom = molecule((mol) => {
  const logger = mol(LoggerMolecule)
  const { sdkContainerAtom } = mol(EvmSdkContainerMoleculeReatom)
  const { address: addressAtom } = mol(WalletConnectMoleculeReatom)

  const acceptCollectionBidActionFactory = () => {
    // Store acceptId outside action for access in cleanup
    let acceptId: string | number | undefined

    const acceptCollectionBidAction = action(
      async ({
        selectedNftsAtom,
        offerByNftAtom,
        blockchain,
        capturedSelectedNfts,
        capturedOfferByNft,
      }: AcceptCollectionBidActionParams) => {
        const container = sdkContainerAtom()
        const from = addressAtom()

        if (!from) {
          throw new Error("Wallet address is not available")
        }

        // Use captured data if available (atoms may be destroyed after dialog close)
        // Fall back to reading from atoms if captured data not provided
        const selectedNfts = capturedSelectedNfts ?? selectedNftsAtom()
        const offers = capturedOfferByNft ?? offerByNftAtom()

        if (selectedNfts.length === 0) {
          throw new Error("No NFTs selected")
        }

        const selectedIds = selectedNfts.map((nft) => nft.id)

        const ordersMap = selectedNfts.reduce<Record<ItemId, Order>>(
          (acc, nft) => {
            const offer = offers[nft.id]
            if (offer?.offer) {
              acc[nft.id] = offer.offer.order
            }
            return acc
          },
          {},
        )

        // Execute accept collection bid via Reatom SDK
        const acceptCollectionBid = acceptCollectionBidFactoryReatom(container)
        const request: AcceptCollectionBidRequest = {
          itemIds: selectedIds,
          ordersMap,
          blockchain,
          from,
        }

        const result = await wrap(acceptCollectionBid(request))

        const { errorTransactions, successfulTransactions } = result

        // Process successful transactions
        for (const transactionInfo of successfulTransactions) {
          const { itemId, txId } = transactionInfo
          const nft = selectedNfts.find((nft) => nft.id === itemId)
          if (!nft) continue // Skip if NFT not found

          const eventContext = {
            flow: Flow.AcceptMultipleOffers,
            ...getBaseNftEventContext({
              id: nft.id,
              collection: nft.collection,
            }),
            transactionId: txId,
          }
          logger?.api.pushEvent(TransactionEvent.Completed, eventContext)
          amplitude.track(TransactionEvent.Completed, eventContext)
          sonnerToast.dismiss()
          sonnerToast.custom((id) => (
            <AcceptCollectionBidToast.Success
              nft={nft}
              id={id}
              transactionId={txId}
            />
          ))
        }

        // Process error transactions
        for (const transactionInfo of errorTransactions) {
          const { itemId, error } = transactionInfo
          const nft = selectedNfts.find((nft) => nft.id === itemId)
          if (!nft) continue // Skip if NFT not found

          const eventContext = {
            flow: Flow.AcceptMultipleOffers,
            ...getBaseNftEventContext({
              id: nft.id,
              collection: nft.collection,
            }),
            error: getTransactionErrorMessage(error),
          }
          logger?.api.pushEvent(TransactionEvent.Failed, eventContext)
          amplitude.track(TransactionEvent.Failed, eventContext)

          sonnerToast.custom((id) => (
            <AcceptCollectionBidToast.Error nft={nft} id={id} error={error} />
          ))
        }

        // Dismiss pending toast
        if (acceptId !== undefined) {
          sonnerToast.dismiss(acceptId)
        }

        return result
      },
      "acceptCollectionBidAction",
    ).extend(
      withAsync(),

      // onCall: Called when action starts (replaces onMutate)
      withCallHook(async (values, [params]) => {
        const { selectedNftsAtom, blockchain, capturedSelectedNfts } = params

        // Use captured data if available
        const selectedNfts = capturedSelectedNfts ?? selectedNftsAtom()

        acceptId = sonnerToast.custom(
          (id) => (
            <AcceptCollectionBidToast.Pending nfts={selectedNfts} id={id} />
          ),
          {
            duration: Number.POSITIVE_INFINITY,
          },
        )

        const multipleTransactionEventContext: EventContext = {
          flow: Flow.AcceptMultipleOffers,
          blockchain,
        }

        logger?.api.pushEvent(
          TransactionEvent.Started,
          multipleTransactionEventContext,
        )
        amplitude.track(
          TransactionEvent.Started,
          multipleTransactionEventContext,
        )
      }),
    )

    // onFulfill: Called on success (replaces onSuccess)
    acceptCollectionBidAction.onFulfill.extend(
      withCallHook(({ params, payload }) => {
        const { blockchain } = params[0]

        const multipleTransactionEventContext: EventContext = {
          flow: Flow.AcceptMultipleOffers,
          blockchain,
        }

        logger?.api.pushEvent(
          TransactionEvent.Completed,
          multipleTransactionEventContext,
        )
        amplitude.track(
          TransactionEvent.Completed,
          multipleTransactionEventContext,
        )
      }),
    )

    // onReject: Called on error (replaces onError)
    acceptCollectionBidAction.onReject.extend(
      withCallHook(({ error, params: [paramsValue] }) => {
        console.error("Accept collection bid failed:", error)

        const { selectedNftsAtom, blockchain, capturedSelectedNfts } =
          paramsValue
        // Use captured data if available
        const selectedNfts = capturedSelectedNfts ?? selectedNftsAtom()

        const multipleTransactionEventContext: EventContext = {
          flow: Flow.AcceptMultipleOffers,
          blockchain,
        }

        const context = {
          ...multipleTransactionEventContext,
          error: getTransactionErrorMessage(error),
        }

        logger?.api.pushEvent(TransactionEvent.Failed, context)
        amplitude.track(TransactionEvent.Failed, context)

        sonnerToast.dismiss()
        if (selectedNfts.length > 0) {
          sonnerToast.custom((id) => (
            <AcceptCollectionBidToast.Error
              nft={selectedNfts[0]}
              id={id}
              error={error as Error}
            />
          ))
        }
      }),
    )

    return acceptCollectionBidAction
  }

  return { acceptCollectionBidActionFactory }
})
